import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { UserService } from '../services/users.service';
import { RolesService } from '../services/roles.service';

export default function AdminForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [roles, setRoles] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_id: '',
    status: 'active'
  });

  const initialize = async () => {
    try {
      // Fetch all roles
      const rolesRes = await RolesService.getAllRoles();
      setRoles(rolesRes);
      // Fetch existing admin if edit mode
      if (isEditMode) {
        const userRes = await UserService.getUser(id);
        const userData = userRes?.data || userRes;
        if (userData) {
          setFormData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            email: userData.email || '',
            password: '', // Leave empty on edit so backend ignores if not provided
            role_id: userData.role_id || '',
            status: userData.status || 'active'
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    initialize();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.role_id) {
      toast.error('First name, Last name, Email, and Role are required');
      return;
    }

    if (!isEditMode && !formData.password) {
      toast.error('Password is required for new admins');
      return;
    }

    setIsLoading(true);
    try {
      const payload = { ...formData };

      // Don't send empty password on update
      if (isEditMode && !payload.password) {
        delete payload.password;
      }

      if (isEditMode) {
        await UserService.updateUser(id, payload);
        toast.success('Admin user updated successfully!');
      } else {
        await UserService.createUser(payload);
        toast.success('Admin user created successfully!');
      }
      navigate('/admins');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save admin user');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return <div className="page-content"><p>Loading...</p></div>;
  }

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/admins')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '40px', height: '40px', borderRadius: '50%',
            border: '1px solid var(--border-color)', background: 'white',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} color="var(--text-secondary)" />
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            {isEditMode ? 'Edit Admin User' : 'Add Admin User'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Configure admin credentials and assign RBAC roles
          </p>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', maxWidth: '800px' }}>
        <form onSubmit={handleSubmit}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

            <div className="form-group">
              <label>First Name *</label>
              <input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="e.g. John" required />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="e.g. Doe" required />
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g. john@example.com" required />
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Password {isEditMode && '(Leave blank to keep current)'}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                required={!isEditMode}
              />
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select name="role_id" value={formData.role_id} onChange={handleChange} required>
                <option value="">Select a role...</option>
                {roles.map(r => (
                  <option key={r.id || r._id} value={r.id || r._id}>
                    {r.display_name || r.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

          </div>

          <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
            <button
              type="button"
              onClick={() => navigate('/admins')}
              style={{ padding: '12px 24px', borderRadius: '10px', border: '1px solid var(--border-color)', background: '#F8F9FB', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: '600' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 32px', borderRadius: '10px', border: 'none', background: 'var(--primary-orange)', color: 'white', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}
            >
              <Save size={18} />
              {isLoading ? 'Saving...' : 'Save Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
